// src/pages/Dashboard/Admin/DetailsAdherents/DetailsAdherentDashboard.jsx

import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import {
  FaAddressCard,
  FaCalendarCheck,
  FaClipboardList,
  FaFileAlt,
  FaMedal,
  FaMoneyCheckAlt,
  FaTrophy,
  FaUserCog,
  FaUsers,
} from "react-icons/fa";

import { useAdherentDashboard } from "./hooks/useAdherentDashboard";

import DashboardAdherentHeader from "./components/DashboardAdherentHeader";
import AdherentFiltersCard from "./components/AdherentFiltersCard";
import AdherentKpiCards from "./components/AdherentKpiCards";
import AdherentHeroCard from "./components/AdherentHeroCard";
import AdherentQuickNav from "./components/AdherentQuickNav";
import AdherentCollapsibleSection from "./components/AdherentCollapsibleSection";
import EmptyAdherentState from "./components/EmptyAdherentState";

import IdentityPanel from "./components/IdentityPanel";
import GroupsCategoriesPanel from "./components/GroupsCategoriesPanel";
import ManagersPanel from "./components/ManagersPanel";
import DossierPanel from "./components/DossierPanel";
import ReglementPanel from "./components/ReglementPanel";
import BeltsPanel from "./components/BeltsPanel";
import CompetitionsPanel from "./components/CompetitionsPanel";
import PalmaresPanel from "./components/PalmaresPanel";
import AchatsPanel from "./components/AchatsPanel";
import AttendancePanel from "./components/AttendancePanel";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Détail adhérent
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DetailsAdherentDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const [openSections, setOpenSections] = useState({
    identity: true,
    groups: true,
    managers: true,
    dossier: true,
    reglement: true,
    belts: false,
    competitions: false,
    palmares: false,
    achats: false,
    attendance: false,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const {
    selectedAdherent,
    selectedAdherentId,
    filteredAdherents,
    seasons,
    seasonFilter,
    setSeasonFilter,
    dojos,
    selectedDojo,
    handleDojoChange,
    groups,
    selectedGroup,
    setSelectedGroup,
    loadingPage,
    loadingDetails,
    errorMsg,
    selectAdherent,
    refreshCurrentAdherent,
    roles,
    managers,
    dossier,
    reglement,
    belts,
    competitions,
    palmares,
    achats,
    attendances,
    ageCategory,
    weightCategory,
    stats,
  } = useAdherentDashboard();

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardAdherentHeader />

        <AdherentFiltersCard
          seasons={seasons}
          seasonFilter={seasonFilter}
          setSeasonFilter={setSeasonFilter}
          dojos={dojos}
          selectedDojo={selectedDojo}
          handleDojoChange={handleDojoChange}
          groups={groups}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          adherents={filteredAdherents}
          selectedAdherentId={selectedAdherentId}
          selectAdherent={selectAdherent}
          loading={loadingPage}
          loadingDetails={loadingDetails}
          refreshCurrentAdherent={refreshCurrentAdherent}
          totalFiltered={filteredAdherents.length}
          errorMsg={errorMsg}
        />

        {!selectedAdherent ? (
          <EmptyAdherentState loading={loadingPage || loadingDetails} />
        ) : (
          <>
            <AdherentHeroCard
              adherent={selectedAdherent}
              ageCategory={ageCategory}
              weightCategory={weightCategory}
            />

            <AdherentKpiCards stats={stats} loading={loadingDetails} />

            <AdherentQuickNav />

            <AdherentCollapsibleSection
              id="identity"
              eyebrow="Profil"
              title="Identité & coordonnées"
              description="Informations administratives principales de l’adhérent."
              icon={<FaAddressCard />}
              isOpen={openSections.identity}
              onToggle={() => toggleSection("identity")}
            >
              <IdentityPanel adherent={selectedAdherent} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="groups"
              eyebrow="Club"
              title="Groupes & catégories"
              description="Groupes d’entraînement, catégorie d’âge et catégorie de poids."
              icon={<FaUsers />}
              isOpen={openSections.groups}
              onToggle={() => toggleSection("groups")}
            >
              <GroupsCategoriesPanel
                adherent={selectedAdherent}
                ageCategory={ageCategory}
                weightCategory={weightCategory}
              />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="managers"
              eyebrow="Famille / compte"
              title="Gestionnaires liés"
              description="Utilisateurs responsables ou associés à cet adhérent."
              icon={<FaUserCog />}
              isOpen={openSections.managers}
              onToggle={() => toggleSection("managers")}
            >
              <ManagersPanel managers={managers} roles={roles} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="dossier"
              eyebrow="Inscription"
              title="Dossier d’inscription"
              description="Certificat médical, licence, formulaire signé, Drive et validations."
              icon={<FaFileAlt />}
              isOpen={openSections.dossier}
              onToggle={() => toggleSection("dossier")}
            >
              <DossierPanel dossier={dossier} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="reglement"
              eyebrow="Paiement"
              title="Règlement d’inscription"
              description="Licence, cotisation, réductions, Pass’Sport et statut du règlement."
              icon={<FaClipboardList />}
              isOpen={openSections.reglement}
              onToggle={() => toggleSection("reglement")}
            >
              <ReglementPanel
                reglement={reglement}
                saison={selectedAdherent?.saison_active || seasonFilter}
              />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="belts"
              eyebrow="Progression"
              title="Passages de ceintures"
              description="Historique des grades et passages de ceintures."
              icon={<FaMedal />}
              isOpen={openSections.belts}
              onToggle={() => toggleSection("belts")}
            >
              <BeltsPanel belts={belts} adherent={selectedAdherent} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="competitions"
              eyebrow="Sportif"
              title="Compétitions"
              description="Inscriptions, validations, résultats et classements."
              icon={<FaTrophy />}
              isOpen={openSections.competitions}
              onToggle={() => toggleSection("competitions")}
            >
              <CompetitionsPanel competitions={competitions} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="palmares"
              eyebrow="Résultats"
              title="Palmarès"
              description="Résultats sportifs et observations."
              icon={<FaMedal />}
              isOpen={openSections.palmares}
              onToggle={() => toggleSection("palmares")}
            >
              <PalmaresPanel palmares={palmares} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="achats"
              eyebrow="Boutique"
              title="Achats & commandes"
              description="Achats effectués via les comptes gestionnaires."
              icon={<FaMoneyCheckAlt />}
              isOpen={openSections.achats}
              onToggle={() => toggleSection("achats")}
            >
              <AchatsPanel achats={achats} />
            </AdherentCollapsibleSection>

            <AdherentCollapsibleSection
              id="attendance"
              eyebrow="Présences"
              title="Présences & absences"
              description="Historique de présence de l’adhérent."
              icon={<FaCalendarCheck />}
              isOpen={openSections.attendance}
              onToggle={() => toggleSection("attendance")}
            >
              <AttendancePanel attendances={attendances} />
            </AdherentCollapsibleSection>
          </>
        )}
      </div>

      <ToastContainer />
    </section>
  );
}
